import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable ,  Subject } from 'rxjs';
import { AbstractCRUD, BannerNotificationsService, CRUDService, ExceptionService, UtilsService } from '../../xform-compat';
import { TranslateService } from '@ngx-translate/core';
import { catchError } from 'rxjs/operators';

/**
 * Service class for referencing notes
 */
@Injectable({
  providedIn: 'root'
})
export class ReportGeneratorService extends AbstractCRUD<any> implements CRUDService<any> {
  private reportGeneratorUpdate: Subject<any>;
  constructor(
    http: HttpClient,
    utilsService: UtilsService,
    exceptionService: ExceptionService,
    private bannerNotificationService: BannerNotificationsService,
    private translateService: TranslateService
  ) {
    super(exceptionService, http, utilsService);
    this.reportGeneratorUpdate = new Subject<any>();
  }

  /**
   * @returns {string}
   */
  getResourcePath(): string {
    return '/reports';
  }

  /**
   * Search for an entity based on a search term
   * @param params
   */
  public autocompleteProduct(params: any): Observable<any> {
    const httpParams: HttpParams = params;
    return super.getHTTP().get(`/api/rms/autocomplete/products`, { params: httpParams })
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  /**
   * Generates the report for the company
   * @param reportType
   * @param queryParam
   */
  public downloadReport (reportType: string, queryParam: string, reportDate: string) {
    const headers = new HttpHeaders({'Accept': 'application/octet-stream'});
    super.getHTTP().get(`/api/rms/reports/${reportType}/csv${queryParam}`, {headers: headers, responseType: 'text'})
      .subscribe(
        (value: any) => {
          const universalBOM = '\uFEFF';
          const hiddenElement = document.createElement('a');
          hiddenElement.href = 'data:text/csv; charset=utf-8,' + encodeURIComponent(universalBOM + value);
          hiddenElement.target = '_blank';
          hiddenElement.download = reportDate ? `${reportType}-report-${reportDate}.csv` : `${reportType}-report.csv`;
          document.body.appendChild(hiddenElement);
          hiddenElement.click();
          this.bannerNotificationService.success(this.translateService.instant('GENERICS.DOWNLOADING'));
        },
        () => this.bannerNotificationService.error(this.translateService.instant('GENERICS.DOWNLOAD_ERROR'))
      );
  }

  /**
   * Downloads an exported report based on date range
   * @param {string} reportType
   * @param queryParam
   * @returns {Observable<Blob>}
   */
  public downloadReportAsZipFile(reportType: string, queryParam: string): Observable<Blob> {
    const headers = new HttpHeaders({ 'Accept': 'application/octet-stream' });

    return super.getHTTP()
      .get(`/api/rms/reports/${reportType}/export${queryParam}`, { headers, responseType: 'blob' })
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  /**
   * Generates the report for the company
   * @param reportType
   * @param queryParam
   * @param params
   */
  public getReport (reportType: string, queryParam: string, params?: any): Observable<any> {
    const httpParams: HttpParams = params;
    return super.getHTTP().get(`/api/rms/reports/${reportType}${queryParam}`, { params: httpParams })
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  /**
   * Generates the preview report for the company
   * @param reportType
   * @param queryParam
   * @param params
   */
  public getReportPreview (reportType: string, queryParam: string, params?: any): Observable<any> {
    const httpParams: HttpParams = params;
    return super.getHTTP().get(`/api/rms/reports/${reportType}/preview${queryParam}`, { params: httpParams })
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  /**
   * Gets the fields for the report
   */
  public getReportFields (params?: any): Observable<any> {
    const httpParams: HttpParams = params;
    return super.getHTTP().get(`/api/rms/reports/fields`, { params: httpParams })
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  /**
   * Sends a message to the subscribed object on the component
   * @param message
   */
  public sendMessage(message: string) {
    this.reportGeneratorUpdate.next({ text: message });
  }

  /**
   * Observable to listen when a message is sent
   */
  public getMessage(): Observable<any> {
    return this.reportGeneratorUpdate.asObservable();
  }

}
